/**
 * Challenge Type System and Interfaces
 * Defines the core types and interfaces for forward and inverse challenge modes
 */

/**
 * Enum defining the two challenge types supported by the system
 */
export enum ChallengeType {
  FORWARD = 'forward',  // "4 of C" → "F"
  INVERSE = 'inverse'   // "F is 4th in scale of..." → "C"
}

/**
 * Enhanced Challenge interface that supports both forward and inverse modes
 */
export interface Challenge {
  type: ChallengeType;      // The type of challenge (forward or inverse)
  key: string;              // The actual key of the scale
  degree: number;           // Scale degree (2-7)
  note: string;             // The note at that degree
  correctAnswer: string;    // Expected answer (note for forward, key for inverse)
  timestamp: number;        // When the challenge was created
}

/**
 * Strategy interface for handling different challenge types
 * Uses the Strategy pattern to encapsulate challenge-specific logic
 */
export interface ChallengeStrategy {
  /**
   * Generate a new challenge of this type
   * @param key - Optional specific key to use, otherwise random
   * @returns Generated challenge
   */
  generateChallenge(key?: string): Challenge;

  /**
   * Format the challenge text for display in the UI
   * @param challenge - The challenge to format
   * @returns Display text string
   */
  formatDisplayText(challenge: Challenge): string;

  /**
   * Format the challenge text for speech synthesis (TTS)
   * @param challenge - The challenge to format
   * @returns Speech text string with proper pronunciation
   */
  formatSpeechText(challenge: Challenge): string;

  /**
   * Validate a user's answer against the challenge
   * @param challenge - The challenge being answered
   * @param answer - User's answer (note name or key name)
   * @returns True if the answer is correct
   */
  validateAnswer(challenge: Challenge, answer: string | null): boolean;

  /**
   * Get the correct answer in speech format for TTS feedback
   * @param challenge - The challenge that was answered incorrectly
   * @returns Speech text for the correct answer
   */
  getCorrectAnswerSpeech(challenge: Challenge): string;
}